import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Container from '@grnhse/seedling/lib/birch/components/container';
import BorderlessContainer from 'talent_matching/components/shared/borderless_container';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import {
  Body,
  InlineMarkdown,
  Link,
  Label,
  SecondaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import DefinitelyNotIcon from '@grnhse/seedling/lib/birch/icons/definitely_not';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import ForwardIcon from '@grnhse/seedling/lib/birch/icons/forward';
import DocumentCard from 'talent_matching/components/shared/document_card';
import ScorecardRatingPills from './scorecard_rating_pills';
import type {
  RediscoveryApplicationHistoryItem,
  Document,
} from 'talent_matching/types';
import { formatShortDate, formatLongDate } from 'talent_search/lib/utils';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import noop from 'lodash/noop';

const t = withLocalePrefix(
  'talent_matching.panel.rediscovery_application_history'
);

type Props = {
  application: RediscoveryApplicationHistoryItem;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

const RowDetail = styled(SecondaryBody)`
  white-space: nowrap;
`;

const StyledSecondaryBody = styled(SecondaryBody)<{ $color?: string }>`
  && {
    ${({ $color }) => $color && `color: ${$color};`}
  }
`;

const RediscoveryApplicationCard = ({
  application,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
}: Props) => {
  const { userDateFormat } = useUserContext();

  const {
    hiring_plan_name,
    url,
    hiring_plan_url,
    requisition_id,
    application_stage_name,
    days_in_stage,
    applied_date,
    source,
    recruiter,
    rejected_on,
    hired_on,
    scorecard_ratings_summary,
    attachments = [],
  } = application;

  const isRejected = rejected_on !== null;
  const isHired = hired_on !== null;

  const stageDisplay = application_stage_name
    ? days_in_stage !== null && days_in_stage > 0
      ? t('stage_with_days', {
          stage: application_stage_name,
          days: days_in_stage,
        })
      : application_stage_name
    : null;

  return (
    <Container>
      <Stack spacing={2}>
        <Stack spacing={0}>
          <Link href={hiring_plan_url} target="_blank">
            {hiring_plan_name}
          </Link>
          {requisition_id && <Body metadata>{requisition_id}</Body>}
        </Stack>

        {isRejected && (
          <BorderlessContainer filled>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DefinitelyNotIcon size="sm" color={FullPalette.POPPY_600} />
                <StyledSecondaryBody $color={FullPalette.POPPY_600}>
                  <InlineMarkdown
                    text={
                      rejected_on
                        ? t('rejected_on', {
                            date: formatShortDate(rejected_on, userDateFormat),
                          })
                        : t('rejected')
                    }
                  />
                </StyledSecondaryBody>
              </Stack>
            </Stack>
          </BorderlessContainer>
        )}

        {isHired && (
          <BorderlessContainer filled>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon size="sm" color={FullPalette.GREEN_700} />
                <StyledSecondaryBody $color={FullPalette.GREEN_700}>
                  <InlineMarkdown
                    text={
                      hired_on
                        ? t('hired_on', {
                            date: formatShortDate(hired_on, userDateFormat),
                          })
                        : t('hired')
                    }
                  />
                </StyledSecondaryBody>
              </Stack>
            </Stack>
          </BorderlessContainer>
        )}

        <Divider />

        <Stack spacing={0.5}>
          {stageDisplay && (
            <Stack direction="row" justifyContent="space-between">
              <Label>{t('stage_label')}</Label>
              <RowDetail>{stageDisplay}</RowDetail>
            </Stack>
          )}
          {applied_date && (
            <Stack direction="row" justifyContent="space-between">
              <Label>{t('applied_label')}</Label>
              <RowDetail>
                {formatLongDate(applied_date, userDateFormat)}
              </RowDetail>
            </Stack>
          )}
          {source && (
            <Stack direction="row" justifyContent="space-between">
              <Label>{t('source_label')}</Label>
              <RowDetail>{source}</RowDetail>
            </Stack>
          )}
          {recruiter && (
            <Stack direction="row" justifyContent="space-between">
              <Label>{t('recruiter_label')}</Label>
              <RowDetail>{recruiter}</RowDetail>
            </Stack>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Label>{t('interview_ratings')}</Label>
            <Link href={`${url}#scorecard`} target="_blank" secondary>
              {t('scorecards_link')}
            </Link>
          </Stack>
          <ScorecardRatingPills summary={scorecard_ratings_summary} />
        </Stack>

        <Divider />

        {attachments.length > 0 && (
          <Stack spacing={1}>
            <Body>{t('documents')}</Body>
            {attachments.map((doc, index) => (
              <DocumentCard
                key={doc.id ?? index}
                document={doc}
                selected={!!activeDocument && doc.id === activeDocument.id}
                canDownloadCandidateData={canDownloadCandidateData}
                isRediscovery
                onClick={
                  doc.previewable !== false && onOpenDocument
                    ? () => onOpenDocument(doc)
                    : undefined
                }
              />
            ))}
          </Stack>
        )}

        <Stack alignItems="flex-start">
          <Link href={url} target="_blank">
            <SecondaryButton
              onClick={() => noop}
              icon={() => (
                <ForwardIcon size="sm" color={FullPalette.GREEN_700} />
              )}
              iconPosition="right"
            >
              {t('go_to_application')}
            </SecondaryButton>
          </Link>
        </Stack>
      </Stack>
    </Container>
  );
};

export default RediscoveryApplicationCard;
